import React from 'react';
import PropTypes from 'prop-types';
import Icon from '../icons/Icons';
import styles from './Select.module.css';

/**
 * Select (Dropdown) component
 * - Mimics a clean input with a light border and right-aligned caret
 *
 * Props:
 * - label?: string
 * - required?: boolean
 * - value: string | number
 * - onChange: (e) => void
 * - placeholder?: string (shown as a disabled option if provided)
 * - options?: Array<{ value: string | number, label: string }>
 * - children?: ReactNode (custom <option> elements)
 * - id?: string
 * - name?: string
 * - className?: string
 */
export function Select({
  label,
  required = false,
  value,
  onChange,
  placeholder,
  options,
  children,
  id,
  name,
  className,
  ...rest
}) {
  return (
    <div className={`${styles.wrapper}${className ? ' ' + className : ''}`}>
      {label && (
        <label className={styles.label} htmlFor={id || name}>
          {label}
          {required && <small> (required)</small>}
        </label>
      )}
      <div className={styles.control}>
        <select
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          className={styles.select}
          {...rest}
        >
          {placeholder !== undefined && (
            <option value="" disabled={true} hidden={true}>
              {placeholder}
            </option>
          )}
          {options && options.map((opt) => (
            <option key={String(opt.value)} value={opt.value}>
              {opt.label}
            </option>
          ))}
          {children}
        </select>
        <span className={styles.caret} aria-hidden>
          <Icon name="arrows/keyboard_arrow_down" width={20} height={20} />
        </span>
      </div>
    </div>
  );
}

Select.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
    })
  ),
  children: PropTypes.node,
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
};

export default Select;
